import EventEmitter from 'events'
import debounce from 'lodash/debounce'

import { ENV } from '@/config'
import storage from '@/utils/storage'
import { sendError } from '@/utils/rollbar'
import { SCOPE } from '@/constants'
import gatekeeperRequest from '@/utils/gatekeeperRequest'

import { type FetchNewTokenDataOptions, type TokenData, isTokenDataKey } from './types'
import TokenError from './TokenError'

const CHANGE_EVENT = 'tokens:sync'
const JWT_EXPIRED_EVENT = 'tokens:jwtExpired'
const ERROR_EVENT = 'tokens:error'
const RENEWED_EXPIRED_JWT = 'tokens:renewedExpiredJwt'

const PREFIX = 'neat'

const ACCOUNT_ID = `${PREFIX}::global::account`
const JWT = `${PREFIX}::global::jwt`
const JWT_EXPIRES_AT = `${PREFIX}::global::jwtExpiresAt`
const REFRESH_JWT = `${PREFIX}::global::refreshJWT`
const REFRESH_JWT_EXPIRES_AT = `${PREFIX}::global::refreshJWTExpiresAt`
const OAUTH_TOKEN = `${PREFIX}::global::token`

const MAX_JWT_REFRESH_WAIT_TIME = 2000
const JWT_REFRESH_WAIT_INTERVAL = 100

export const REQUIRED_JWT_STORAGE_KEYS = [JWT, REFRESH_JWT, JWT_EXPIRES_AT, REFRESH_JWT_EXPIRES_AT]

export const tokenStorageMap = {
  accountId: ACCOUNT_ID,
  jwt: JWT,
  jwtExpiresAt: JWT_EXPIRES_AT,
  refreshJWT: REFRESH_JWT,
  refreshJWTExpiresAt: REFRESH_JWT_EXPIRES_AT,
  oauthToken: OAUTH_TOKEN,
} as const

export const getOauthTokenFromStorage = () => storage.get(OAUTH_TOKEN) || undefined

export const getJWTFromStorage = () => storage.get(JWT) || undefined

export const getRefreshJWTFromStorage = () => storage.get(REFRESH_JWT) || undefined

export const getTokenDataFromStorage = (): TokenData => ({
  oauthToken: getOauthTokenFromStorage(),
  jwt: getJWTFromStorage(),
  jwtExpiresAt: Number(storage.get(JWT_EXPIRES_AT)) || undefined,
  refreshJWT: getRefreshJWTFromStorage(),
  refreshJWTExpiresAt: Number(storage.get(REFRESH_JWT_EXPIRES_AT)) || undefined,
})

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

const getIsTokenExpired = (expiresAtStorageKey = JWT_EXPIRES_AT) => {
  if (ENV === 'test') return false
  const expiresAt = storage.get(expiresAtStorageKey)
  if (!expiresAt) return true
  const nowInSeconds = Math.ceil(Date.now() / 1000)
  const timeBuffer = expiresAtStorageKey === REFRESH_JWT_EXPIRES_AT ? 0 : 2
  return nowInSeconds >= (Number(expiresAt) - timeBuffer)
}

class Tokens extends EventEmitter {
  didJWTExpire = false

  isFetching = false

  constructor() {
    super()
    this.on(JWT_EXPIRED_EVENT, this.debouncedHandleExpiredJwtEvent)
  }

  onSync(cb: () => void) {
    return this.on(CHANGE_EVENT, cb)
  }

  removeOnSync(cb: () => void) {
    return this.removeListener(CHANGE_EVENT, cb)
  }

  onError(cb: (error?: TokenError) => void) {
    return this.on(ERROR_EVENT, cb)
  }

  removeOnError(cb: () => void) {
    return this.removeListener(ERROR_EVENT, cb)
  }

  onRenewedExpiredJwt(cb: () => void) {
    return this.on(RENEWED_EXPIRED_JWT, cb)
  }

  removeOnRenewedExpiredJwt(cb: () => void) {
    return this.removeListener(RENEWED_EXPIRED_JWT, cb)
  }

  emitChangeEvent() {
    this.emit(CHANGE_EVENT)
  }

  emitErrorEvent(error?: TokenError) {
    this.emit(ERROR_EVENT, error)
  }

  handleExpiredJwtEvent = async () => {
    try {
      const refreshTokenExpired = getIsTokenExpired(REFRESH_JWT_EXPIRES_AT)
      if (refreshTokenExpired) {
        throw new TokenError('refresh_token expired', 'REFRESH_TOKEN_EXPIRED')
      } else {
        await this.renewJWT()
        this.emit(RENEWED_EXPIRED_JWT)
      }
    } catch (err) {
      this.emitErrorEvent(err instanceof TokenError ? err : undefined)
    }
  }

  debouncedHandleExpiredJwtEvent = debounce(this.handleExpiredJwtEvent)

  verifyOrRefreshJWT = async () => {
    if (this.didJWTExpire || this.isFetching) return this.waitForJwtToBeRefreshed()
    const isExpired = getIsTokenExpired()
    if (isExpired) {
      if (!this.didJWTExpire) {
        this.didJWTExpire = true
        this.emit(JWT_EXPIRED_EVENT)
      }
      return this.waitForJwtToBeRefreshed()
    }
    return undefined
  }

  waitForJwtToBeRefreshed = async (currentWaitTime = 0): Promise<void> => {
    if ((this.didJWTExpire || this.isFetching) && currentWaitTime < MAX_JWT_REFRESH_WAIT_TIME) {
      await sleep(JWT_REFRESH_WAIT_INTERVAL)
      return this.waitForJwtToBeRefreshed(currentWaitTime + JWT_REFRESH_WAIT_INTERVAL)
    }
    return undefined
  }

  getCurrentOrNewJWT = async () => {
    await this.verifyOrRefreshJWT()
    return getJWTFromStorage()
  }

  setTokenData = (data: TokenData) => {
    Object.entries(data).forEach(([key, value]) => {
      if (isTokenDataKey(key)) {
        storage.set(tokenStorageMap[key], String(value)) }
    })
    this.emitChangeEvent()
  }

  clearToken = (storageId: string) => {
    storage.remove(storageId)
    this.emitChangeEvent()
  }

  clearAllTokenData = () => {
    Object.values(tokenStorageMap).forEach((storageId) => {
      storage.remove(storageId)
    })
    this.emitChangeEvent()
  }

  async fetchNewTokenData(options: FetchNewTokenDataOptions = {}) {
    const {
      accountId: accountIdOption,
      requireAccountId = false,
      url = '/v3/jwt',
      useOauthToken = true,
      useJWT = false,
      useRefreshJWT = false,
      useScope = false,
    } = options
    try {
      this.debouncedHandleExpiredJwtEvent.cancel()
      this.isFetching = true
      const accountId = accountIdOption || storage.get(ACCOUNT_ID) || undefined
      if (requireAccountId && !accountId) {
        throw new TokenError('Missing account id for token request', 'MISSING_ACCOUNT_ID', undefined, url)
      }
      const {
        data: {
          token: jwt,
          expiresAt: jwtExpiresAt,
          refreshToken: refreshJWT,
          refreshTokenExpiresAt: refreshJWTExpiresAt,
        },
      } = await gatekeeperRequest({
        method: 'POST',
        url,
        data: {
          ...accountId ? { accountId } : {},
          ...useOauthToken ? { oauthToken: getOauthTokenFromStorage() } : {},
          ...useJWT ? { token: getJWTFromStorage() } : {},
          ...useRefreshJWT ? { refreshToken: getRefreshJWTFromStorage() } : {},
          ...useScope ? { scope: SCOPE } : {},
        },
      })
      this.setTokenData({ jwt, jwtExpiresAt, refreshJWT, refreshJWTExpiresAt })
    } catch (err) {
      sendError(err, 'Error fetching new token data', url)
      let message = ''
      let type = ''
      let status
      if (err && typeof err === 'object') {
        if ('message' in err && typeof err.message === 'string') message = err.message
        if ('type' in err && typeof err.type === 'string') type = err.type
        if ('status' in err && typeof err.status === 'number') status = err.status
      }
      throw new TokenError(message, type, status, url)
    } finally {
      this.didJWTExpire = false
      this.isFetching = false
    }
  }

  fetchJWT = (options: Pick<FetchNewTokenDataOptions, 'accountId'> = {}) => this.fetchNewTokenData({
    ...options,
    useScope: true,
    requireAccountId: true,
  })

  renewJWT = () => this.fetchNewTokenData({
    url: '/v3/renew',
    useOauthToken: false,
    useJWT: true,
    useRefreshJWT: true,
  })
}

export default new Tokens()
